import React, { useCallback } from 'react';
import styles from '../styles/Landing.module.css';
import { LANDING_STRINGS } from '../constants/stringConstants';
import { LandingSection } from '../types/enums';

interface LandingProps {
  onSignIn: () => void;
  onGetStarted: () => void;
  onPatientSignup: () => void;
}

const Icon: React.FC<{ name: string; className?: string }> = ({ name, className }) => (
  <span className={`${styles.materialIcon}${className ? ` ${className}` : ''}`} aria-hidden="true">
    {name}
  </span>
);

const Landing: React.FC<LandingProps> = ({ onSignIn, onGetStarted, onPatientSignup }) => {
  const { NAV, HERO, STATS, PROBLEM, FEATURES, HOW_IT_WORKS, FOR_DOCTORS, TECH, CTA_BANNER, FOOTER, ARIA } =
    LANDING_STRINGS;

  const scrollTo = useCallback((id: LandingSection) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleHomeClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const features = [
    { title: FEATURES.F1_TITLE, body: FEATURES.F1_BODY, icon: FEATURES.F1_ICON },
    { title: FEATURES.F2_TITLE, body: FEATURES.F2_BODY, icon: FEATURES.F2_ICON },
    { title: FEATURES.F3_TITLE, body: FEATURES.F3_BODY, icon: FEATURES.F3_ICON },
    { title: FEATURES.F4_TITLE, body: FEATURES.F4_BODY, icon: FEATURES.F4_ICON },
    { title: FEATURES.F5_TITLE, body: FEATURES.F5_BODY, icon: FEATURES.F5_ICON },
    { title: FEATURES.F6_TITLE, body: FEATURES.F6_BODY, icon: FEATURES.F6_ICON },
  ];

  const steps = [
    { number: HOW_IT_WORKS.STEP_1_NUMBER, title: HOW_IT_WORKS.STEP_1_TITLE, body: HOW_IT_WORKS.STEP_1_BODY },
    { number: HOW_IT_WORKS.STEP_2_NUMBER, title: HOW_IT_WORKS.STEP_2_TITLE, body: HOW_IT_WORKS.STEP_2_BODY },
    { number: HOW_IT_WORKS.STEP_3_NUMBER, title: HOW_IT_WORKS.STEP_3_TITLE, body: HOW_IT_WORKS.STEP_3_BODY },
  ];

  const fdItems = [
    { icon: 'sell', title: FOR_DOCTORS.POINT_1_TITLE, body: FOR_DOCTORS.POINT_1_BODY },
    { icon: 'cloud_download', title: FOR_DOCTORS.POINT_2_TITLE, body: FOR_DOCTORS.POINT_2_BODY },
    { icon: 'devices', title: FOR_DOCTORS.POINT_3_TITLE, body: FOR_DOCTORS.POINT_3_BODY },
    { icon: 'flag', title: FOR_DOCTORS.POINT_4_TITLE, body: FOR_DOCTORS.POINT_4_BODY },
  ];

  const techChips = [
    { icon: 'code', label: TECH.ITEMS.FRONTEND },
    { icon: 'dns', label: TECH.ITEMS.BACKEND },
    { icon: 'lock', label: TECH.ITEMS.AUTH },
    { icon: 'cloud', label: TECH.ITEMS.HOSTING },
    { icon: 'database', label: TECH.ITEMS.DATABASE },
    { icon: 'palette', label: TECH.ITEMS.DESIGN },
  ];

  const stats = [
    { value: STATS.STAT_1_VALUE, label: STATS.STAT_1_LABEL },
    { value: STATS.STAT_2_VALUE, label: STATS.STAT_2_LABEL },
    { value: STATS.STAT_3_VALUE, label: STATS.STAT_3_LABEL },
    { value: STATS.STAT_4_VALUE, label: STATS.STAT_4_LABEL },
  ];

  return (
    <div className={styles.page}>
      {/* ====== NAV ====== */}
      <header className={styles.nav}>
        <div className={`${styles.container} ${styles.navInner}`}>
          <a href="/" onClick={handleHomeClick} className={styles.brand} aria-label={ARIA.GO_TO_HOME}>
            <span className={styles.brandMark}>
              <Icon name="health_and_safety" />
            </span>
            {NAV.BRAND}
          </a>

          <nav className={styles.navLinks} aria-label="Primary">
            <button type="button" className={styles.navLink} onClick={() => scrollTo(LandingSection.FEATURES)}>
              {NAV.LINK_FEATURES}
            </button>
            <button type="button" className={styles.navLink} onClick={() => scrollTo(LandingSection.HOW_IT_WORKS)}>
              {NAV.LINK_HOW_IT_WORKS}
            </button>
            <button type="button" className={styles.navLink} onClick={() => scrollTo(LandingSection.FOR_DOCTORS)}>
              {NAV.LINK_FOR_DOCTORS}
            </button>
          </nav>

          <div className={styles.navActions}>
            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSignIn}>
              {NAV.SIGN_IN}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onGetStarted}>
              {NAV.GET_STARTED}
              <Icon name="arrow_forward" className={styles.btnIcon} />
            </button>
          </div>
        </div>
      </header>

      {/* ====== HERO ====== */}
      <section id={LandingSection.HERO} className={styles.hero}>
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={`${styles.container} ${styles.heroGrid}`}>
          <div>
            <span className={styles.heroPill}>
              <span className={styles.pillDot} />
              {HERO.PILL}
            </span>
            <h1 className={styles.heroHeadline}>
              {HERO.HEADLINE_LINE_1}
              <br />
              <span className={styles.accent}>{HERO.HEADLINE_LINE_2}</span>
            </h1>
            <p className={styles.heroSub}>{HERO.SUBHEADLINE}</p>
            <div className={styles.heroCtas}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}
                onClick={onPatientSignup}
              >
                {HERO.PATIENT_CTA}
                <Icon name="arrow_forward" className={styles.btnIcon} />
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary} ${styles.btnLg}`}
                onClick={onGetStarted}
              >
                <Icon name="stethoscope" className={styles.btnIcon} />
                {HERO.DOCTOR_CTA}
              </button>
            </div>
            <p className={styles.heroTrust}>{HERO.TRUST_LINE}</p>
          </div>

          {/* Stylized dashboard mock */}
          <div className={styles.heroVisual} aria-hidden="true">
            <div className={styles.mockChrome}>
              <span className={styles.mockDot} />
              <span className={styles.mockDot} />
              <span className={styles.mockDot} />
              <span className={styles.mockUrl}>{LANDING_STRINGS.HERO.MOCK_URL}</span>
            </div>
            <div className={styles.mockBody}>
              <div className={styles.mockSide}>
                <span className={`${styles.mockSideItem} ${styles.active}`} />
                <span className={styles.mockSideItem} />
                <span className={styles.mockSideItem} />
                <span className={styles.mockSideItem} />
              </div>
              <div className={styles.mockMain}>
                <div>
                  <div className={styles.mockGreeting}>{HERO.MOCK_HEADER}</div>
                  <div className={styles.mockTitle}>{HERO.MOCK_GREETING}</div>
                </div>
                <div className={styles.mockStats}>
                  <div className={styles.mockStatCard}>
                    <div className={styles.mockStatLabel}>{HERO.MOCK_STAT_TODAY}</div>
                    <div className={styles.mockStatValue}>{HERO.MOCK_STAT_TODAY_VALUE}</div>
                  </div>
                  <div className={styles.mockStatCard}>
                    <div className={styles.mockStatLabel}>{HERO.MOCK_STAT_PATIENTS}</div>
                    <div className={styles.mockStatValue}>{HERO.MOCK_STAT_PATIENTS_VALUE}</div>
                  </div>
                  <div className={styles.mockStatCard}>
                    <div className={styles.mockStatLabel}>{HERO.MOCK_STAT_REVENUE}</div>
                    <div className={styles.mockStatValue}>{HERO.MOCK_STAT_REVENUE_VALUE}</div>
                  </div>
                </div>
                <div className={styles.mockListTitle}>{HERO.MOCK_LIST_TITLE}</div>
                <div className={styles.mockList}>
                  <div className={styles.mockRow}>
                    <span className={styles.mockAvatar}>AC</span>
                    <div className={styles.mockRowText}>
                      <div className={styles.mockRowName}>{HERO.MOCK_ROW_1_NAME}</div>
                      <div className={styles.mockRowType}>{HERO.MOCK_ROW_1_TYPE}</div>
                    </div>
                    <span className={styles.mockBadge}>{HERO.MOCK_ROW_1_STATUS}</span>
                  </div>
                  <div className={styles.mockRow}>
                    <span className={styles.mockAvatar}>RS</span>
                    <div className={styles.mockRowText}>
                      <div className={styles.mockRowName}>{HERO.MOCK_ROW_2_NAME}</div>
                      <div className={styles.mockRowType}>{HERO.MOCK_ROW_2_TYPE}</div>
                    </div>
                    <span className={`${styles.mockBadge} ${styles.warn}`}>{HERO.MOCK_ROW_2_STATUS}</span>
                  </div>
                  <div className={styles.mockRow}>
                    <span className={styles.mockAvatar}>PI</span>
                    <div className={styles.mockRowText}>
                      <div className={styles.mockRowName}>{HERO.MOCK_ROW_3_NAME}</div>
                      <div className={styles.mockRowType}>{HERO.MOCK_ROW_3_TYPE}</div>
                    </div>
                    <span className={styles.mockBadge}>{HERO.MOCK_ROW_3_STATUS}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== STATS STRIP ====== */}
      <section className={styles.stats}>
        <div className={`${styles.container} ${styles.statsGrid}`}>
          {stats.map((s) => (
            <div key={s.label}>
              <div className={styles.statValue}>{s.value}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ====== PROBLEM ====== */}
      <section className={styles.section}>
        <div className={`${styles.container} ${styles.problemGrid}`}>
          <div className={styles.problemNarrative}>
            <span className={styles.sectionEyebrow}>{PROBLEM.EYEBROW}</span>
            <h2 className={styles.sectionHeadline}>{PROBLEM.HEADLINE}</h2>
            <p>{PROBLEM.BODY}</p>
          </div>
          <ul className={styles.problemList}>
            {[PROBLEM.POINT_1, PROBLEM.POINT_2, PROBLEM.POINT_3, PROBLEM.POINT_4].map((p) => (
              <li key={p} className={styles.problemItem}>
                <span className={styles.problemBullet}>
                  <Icon name="close" />
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ====== FEATURES ====== */}
      <section id={LandingSection.FEATURES} className={styles.section}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{FEATURES.EYEBROW}</span>
            <h2 className={styles.sectionHeadline}>{FEATURES.HEADLINE}</h2>
            <p className={styles.sectionSub}>{FEATURES.SUBHEADLINE}</p>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((f) => (
              <article key={f.title} className={styles.featureCard}>
                <div className={styles.featureIcon}>
                  <Icon name={f.icon} />
                </div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureBody}>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section id={LandingSection.HOW_IT_WORKS} className={styles.section}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{HOW_IT_WORKS.EYEBROW}</span>
            <h2 className={styles.sectionHeadline}>{HOW_IT_WORKS.HEADLINE}</h2>
          </div>
          <div className={styles.stepsGrid}>
            {steps.map((s) => (
              <article key={s.number} className={styles.stepCard}>
                <div className={styles.stepNumber}>{s.number}</div>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepBody}>{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ====== FOR DOCTORS ====== */}
      <section id={LandingSection.FOR_DOCTORS} className={`${styles.section} ${styles.forDoctors}`}>
        <div className={`${styles.container} ${styles.fdGrid}`}>
          <div className={styles.fdHeader}>
            <span className={styles.sectionEyebrow}>{FOR_DOCTORS.EYEBROW}</span>
            <h2 className={styles.sectionHeadline}>{FOR_DOCTORS.HEADLINE}</h2>
            <p className={styles.sectionSub}>{FOR_DOCTORS.SUBHEADLINE}</p>
          </div>
          <div className={styles.fdList}>
            {fdItems.map((item) => (
              <div key={item.title} className={styles.fdItem}>
                <div className={styles.fdItemTitle}>
                  <Icon name={item.icon} />
                  {item.title}
                </div>
                <div className={styles.fdItemBody}>{item.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== TECH STACK ====== */}
      <section id={LandingSection.TECH} className={styles.section}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{TECH.EYEBROW}</span>
            <h2 className={styles.sectionHeadline}>{TECH.HEADLINE}</h2>
            <p className={styles.sectionSub}>{TECH.SUBHEADLINE}</p>
          </div>
          <div className={styles.techGrid}>
            {techChips.map((chip) => (
              <div key={chip.label} className={styles.techChip}>
                <Icon name={chip.icon} />
                {chip.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== CTA BANNER ====== */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.ctaBanner}>
            <h2 className={styles.ctaHeadline}>{CTA_BANNER.HEADLINE}</h2>
            <p className={styles.ctaSub}>{CTA_BANNER.SUBHEADLINE}</p>
            <div className={styles.ctaActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnLg} ${styles.ctaPrimary}`}
                onClick={onGetStarted}
              >
                {CTA_BANNER.PRIMARY_CTA}
                <Icon name="arrow_forward" className={styles.btnIcon} />
              </button>
              <a
                className={`${styles.btn} ${styles.btnLg} ${styles.ctaSecondary}`}
                href={`mailto:${FOOTER.CONTACT_EMAIL}`}
              >
                <Icon name="mail" className={styles.btnIcon} />
                {CTA_BANNER.SECONDARY_CTA}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <footer id={LandingSection.CONTACT} className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.footerGrid}>
            <div className={styles.footerBrand}>
              <a href="/" onClick={handleHomeClick} className={styles.brand} aria-label={ARIA.GO_TO_HOME}>
                <span className={styles.brandMark}>
                  <Icon name="health_and_safety" />
                </span>
                {NAV.BRAND}
              </a>
              <p className={styles.footerTagline}>{FOOTER.TAGLINE}</p>
              <div className={styles.footerMeta}>
                <a href={`mailto:${FOOTER.CONTACT_EMAIL}`}>{FOOTER.CONTACT_EMAIL}</a>
                <span aria-hidden="true">·</span>
                <span>{FOOTER.LOCATION}</span>
              </div>
            </div>

            <div>
              <div className={styles.footerColTitle}>{FOOTER.COL_PRODUCT}</div>
              <ul className={styles.footerLinks}>
                <li>
                  <button type="button" className={styles.footerLink} onClick={() => scrollTo(LandingSection.FEATURES)}>
                    {FOOTER.LINK_FEATURES}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLink} onClick={onGetStarted}>
                    {FOOTER.LINK_PRICING}
                  </button>
                </li>
                <li>
                  <button type="button" className={styles.footerLink} onClick={() => scrollTo(LandingSection.HOW_IT_WORKS)}>
                    {FOOTER.LINK_ROADMAP}
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <div className={styles.footerColTitle}>{FOOTER.COL_COMPANY}</div>
              <ul className={styles.footerLinks}>
                <li>
                  <button type="button" className={styles.footerLink} onClick={() => scrollTo(LandingSection.FOR_DOCTORS)}>
                    {FOOTER.LINK_ABOUT}
                  </button>
                </li>
                <li>
                  <a className={styles.footerLink} href={`mailto:${FOOTER.CONTACT_EMAIL}`}>
                    {FOOTER.LINK_CONTACT}
                  </a>
                </li>
                <li>
                  <a className={styles.footerLink} href={`mailto:${FOOTER.CONTACT_EMAIL}?subject=Careers`}>
                    {FOOTER.LINK_CAREERS}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <div className={styles.footerColTitle}>{FOOTER.COL_RESOURCES}</div>
              <ul className={styles.footerLinks}>
                <li>
                  <button type="button" className={styles.footerLink} onClick={() => scrollTo(LandingSection.TECH)}>
                    {FOOTER.LINK_DOCS}
                  </button>
                </li>
                <li>
                  <a
                    className={styles.footerLink}
                    href="https://docx-backend-27188185100.us-central1.run.app/health"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {FOOTER.LINK_STATUS}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className={styles.footerBottom}>
            <span>{FOOTER.COPYRIGHT}</span>
            <div className={styles.footerLegalLinks}>
              <a className={styles.footerLink} href={`mailto:${FOOTER.CONTACT_EMAIL}?subject=Privacy`}>
                {FOOTER.LINK_PRIVACY}
              </a>
              <a className={styles.footerLink} href={`mailto:${FOOTER.CONTACT_EMAIL}?subject=Terms`}>
                {FOOTER.LINK_TERMS}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
